import React from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormDropdown, FormFields, FormTextField, mapToDropdownOption } from "sprint-common-web";

import { DeliveryEndpointType } from "../../../../common/types/DeliveryEndpointType";
import { SigniantDeliveryEndpointDetails, DeliveryEndpointDetails } from "../../listing/DeliveryEndpointItem";
import { useEditSigniantDeliveryEndpointForm } from "./useEditSigniantDeliveryEndpointForm";

export interface EditSigniantDeliveryEndpointFormProps {
    onCancel(): void;
    onSubmit(): void;
    deliveryEndpointId: string;
}

export interface EditSigniantDeliveryEndpointFormInputs {
    name: string;
    type: DeliveryEndpointType.Signiant;
    profileId: string;
    distributionEmail: string;
}

export const EditSigniantDeliveryEndpointForm: React.VFC<EditSigniantDeliveryEndpointFormProps> = ({
    deliveryEndpointId,
    onSubmit,
    onCancel
}: EditSigniantDeliveryEndpointFormProps) => {
    const { deliveryEndpoint, storageProfiles, editDeliveryEndpoint } = useEditSigniantDeliveryEndpointForm(
        deliveryEndpointId
    );

    ensureSigniantDetails(deliveryEndpoint.details);

    const { errors, control, formState, reset, handleSubmit } = useForm<EditSigniantDeliveryEndpointFormInputs>({
        defaultValues: {
            name: deliveryEndpoint.name,
            profileId: deliveryEndpoint.details.destinationStorageProfile.id,
            distributionEmail: deliveryEndpoint.details.distributionEmail
        },
        criteriaMode: "all"
    });

    const handleEditDeliveryEndpoint = async (inputs: EditSigniantDeliveryEndpointFormInputs) => {
        await editDeliveryEndpoint(inputs);
        onSubmit();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleEditDeliveryEndpoint)}>
            <FormFields>
                <FormTextField
                    label="Name"
                    control={control}
                    name="name"
                    errors={errors}
                    rules={{ required: "Enter name" }}
                />
                <FormDropdown
                    label="Profile"
                    name="profileId"
                    options={storageProfiles.map((item) => mapToDropdownOption(item))}
                    control={control}
                    errors={errors}
                    rules={{ required: "Select storage profile" }}
                />
                <FormTextField
                    label="Distribution Email"
                    control={control}
                    name="distributionEmail"
                    errors={errors}
                    rules={{ required: "Enter distribution email" }}
                />
            </FormFields>
            <FormActions
                isLoading={formState.isSubmitting || formState.isValidating}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};

const ensureSigniantDetails: (value: DeliveryEndpointDetails) => asserts value is SigniantDeliveryEndpointDetails = (
    value
) => {
    if (value.type !== DeliveryEndpointType.Signiant) {
        throw Error("Delivery endpoint should be of type 'signiant'");
    }
};
